import { Stack, Typography, Box } from '@mui/material';
import { Skeleton } from '@liminal/web-ui/feedback/Skeleton';
import { useUserKycStatus } from '@/hooks/useUsers';
import { formatDate } from '@liminal/utils/date';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import { CopyableId } from '@liminal/web-ui/display/CopyableId';
import { InitialsAvatar } from '@liminal/web-ui/display/InitialsAvatar';
import {
  kycStatusVariant,
  kycStatusLabel,
  personaStatusLabel,
  personaStatusVariant,
  providerLabel,
  ALL_RAMP_PROVIDERS,
} from '@/components/ui/statusHelpers';
import { SuspensionActions } from './SuspensionActions';
import type { Profile, ProviderStatus } from '@/api/types';
import { brandColors } from '@liminal/brand/palette';

export function UserHeader({ user, loading, userId }: { user: Profile | undefined; loading: boolean; userId: string }) {
  const { data: kycStatus } = useUserKycStatus(userId);

  if (loading || !user) {
    return (
      <Stack direction="row" spacing={2} alignItems="center">
        <Skeleton variant="circle" width={60} height={60} />
        <Stack spacing={1}>
          <Skeleton width={200} height={32} />
          <Skeleton width={280} height={20} />
          <Skeleton width={140} height={20} />
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={2} alignItems="flex-start">
      {/* A profile with no avatar colour yet keeps the cream fallback it had,
          rather than InitialsAvatar's "colour not known yet" skeleton. */}
      <InitialsAvatar
        name={`${user.firstName ?? ''} ${user.lastName ?? ''}`}
        backgroundColor={user.avatarColor ?? brandColors.cream}
        size="lg"
      />

      <Stack spacing={0.5}>
        <Typography variant="h5" fontWeight={600}>
          {user.firstName} {user.lastName}
        </Typography>

        {user.userId && <CopyableId value={user.userId} truncate label="Copy user ID" />}

        <Typography variant="body2" color="text.secondary">
          {user.email}
          {user.phoneNumber && <> &bull; {user.phoneNumber}</>}
        </Typography>

        {user.displayTag && (
          <Box>
            <StatusPill label={`@${user.displayTag}`} tone="info" />
          </Box>
        )}

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 0.5 }}>
          <StatusPill
            label={user.isOnboardingCompleted ? 'Onboarded' : 'Not Onboarded'}
            tone={user.isOnboardingCompleted ? 'success' : 'neutral'}
          />
          {kycStatus && (
            <StatusPill
              label={`Overall KYC: ${kycStatusLabel(kycStatus.overallStatus)}`}
              tone={kycStatusVariant(kycStatus.overallStatus)}
            />
          )}
        </Stack>

        {/* Per-source breakdown chips. Header chip above is the worst-wins
            aggregate; these mirror what the KYC Status card on the Profile
            tab shows so admins don't have to click in to see provider state. */}
        {kycStatus && (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 0.5 }}>
            <StatusPill
              label={`Persona: ${personaStatusLabel(kycStatus.personaStatus)}`}
              tone={personaStatusVariant(kycStatus.personaStatus)}
              size="sm"
            />
            {ALL_RAMP_PROVIDERS.map((provider) => {
              const p = (kycStatus.providers ?? []).find(
                (ps: ProviderStatus) => ps.provider === provider,
              );
              if (!p) return null;
              return (
                <StatusPill
                  key={provider}
                  label={`${providerLabel(provider)}: ${kycStatusLabel(p.status)}`}
                  tone={kycStatusVariant(p.status)}
                  size="sm"
                />
              );
            })}
          </Stack>
        )}

        <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
          {user.createdAt && (
            <Typography variant="caption" color="text.disabled">
              Created: {formatDate(user.createdAt, { month: 'short' })}
            </Typography>
          )}
          {user.updatedAt && (
            <Typography variant="caption" color="text.disabled">
              Updated: {formatDate(user.updatedAt, { month: 'short' })}
            </Typography>
          )}
        </Stack>
      </Stack>

      <Box sx={{ flexGrow: 1 }} />
      <SuspensionActions userId={userId} />
    </Stack>
  );
}
